import React, { useCallback, useState, memo, useRef, useMemo } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Popover } from 'react-tiny-popover';
import { TbMessageCircle } from 'react-icons/tb';
import { IoClose } from 'react-icons/io5';
import * as Styled from './style';
import { useCreateThread, useAddCommentToThread } from '../../../../../services/comment.service';
import CellCommentsList from './CellCommentsList';
import useReportComments from '../../../../../hooks/useReportComments';
import CommentResolvedState from './CommentResolvedState';
import { queryClient } from '../../../../../config/queryClient';
import { QUERY_KEYS } from '../../../../../services/constants';
import { getCustomFundNameForAIFM, getReportType } from '../utils';
import type {
  SchemaCommentMessageResponseSchema,
  SchemaCommentThreadCreateSchema,
  SchemaCommentThreadResponseSchema
} from '../../../../../api/generated/schema';

// We filter comments by fund_id -> section name (tableName) -> fieldOrder -> fieldName (cell's key)
// Example: LCIV Absolute Fund -> Fund Infos -> 2 -> aif_national_code
interface ReportCellCommentProps {
  fundId?: string | null;
  fundName?: string | null;
  tableName?: string | null;
  fieldName?: string | null;
  fieldOrder?: number | string | null;
  // Opt-in lazy mount: when a cell template passes this (boolean), the comment
  // icon/popover/mutation hooks mount only once the cell is hovered or already
  // has comments. Templates that omit it keep the legacy always-rendered behavior.
  cellHovered?: boolean;
}

interface CellCommentBodyProps {
  fundId?: string | null;
  fieldName?: string | null;
  tableName?: string | null;
  fieldOrder?: number | string | null;
  formattedFundName?: string | null;
  workflowId?: string;
  periodId?: string;
  currentReportType: string;
  cellThreadData: SchemaCommentThreadResponseSchema | Record<string, never>;
  cellThreadComments: SchemaCommentMessageResponseSchema[];
  refetchComments: () => Promise<unknown>;
}

interface UseReportCommentsResult {
  cellThreadData: SchemaCommentThreadResponseSchema | Record<string, never>;
  cellThreadComments: SchemaCommentMessageResponseSchema[];
  refetchComments: () => Promise<unknown>;
}

const ReportCellComment = ({
  fundId,
  fundName,
  tableName,
  fieldName,
  fieldOrder,
  cellHovered
}: ReportCellCommentProps) => {
  const { workflowId, periodId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentReportType = getReportType(location?.pathname);
  // We don't show comments in static template
  const staticTemplateModalState = searchParams.get('staticTemplateModalState');
  const isStaticTemplateOpen = staticTemplateModalState === 'open';

  // AIF has a fundId and fundName but AIFM doesn't. So we create a custom fundName in this case
  // fundId would still be null
  const formattedFundName = useMemo(() => {
    const isAIFM =
      searchParams.get('type') === 'AIF001' ||
      searchParams.get('reportType') === 'AIF001' ||
      searchParams.get('fund_id') === 'AIF001';
    let customFundName = fundName;

    if (isAIFM && !fundName) {
      customFundName = getCustomFundNameForAIFM(currentReportType);
    }
    return customFundName;
  }, [fundName, searchParams, currentReportType]);

  const { cellThreadData, cellThreadComments, refetchComments } = useReportComments({
    workflowId,
    periodId,
    fundId,
    fundName: formattedFundName,
    fieldName,
    fieldOrder,
    tableName,
    reportType: currentReportType
  }) as UseReportCommentsResult;

  if (isStaticTemplateOpen) return null;

  // Opt-in lazy mount: only a template that passes `cellHovered` defers the heavy
  // body. Cells that already have comments always render so the count/highlight
  // (sidebar `getElementById`) keep working without a hover.
  const isLazyGated = cellHovered !== undefined;
  const hasComment = cellThreadComments.length > 0;
  if (isLazyGated && !hasComment && !cellHovered) return null;

  return (
    <CellCommentBody
      fundId={fundId}
      fieldName={fieldName}
      tableName={tableName}
      fieldOrder={fieldOrder}
      formattedFundName={formattedFundName}
      workflowId={workflowId}
      periodId={periodId}
      currentReportType={currentReportType}
      cellThreadData={cellThreadData}
      cellThreadComments={cellThreadComments}
      refetchComments={refetchComments}
    />
  );
};

const CellCommentBody = ({
  fundId,
  fieldName,
  tableName,
  fieldOrder,
  formattedFundName,
  workflowId,
  periodId,
  currentReportType,
  cellThreadData,
  cellThreadComments,
  refetchComments
}: CellCommentBodyProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const createThreadMutation = useCreateThread();
  const createCommentInThreadMutation = useAddCommentToThread();
  const isCreatingNewThreadOrComment =
    createThreadMutation.isLoading || createCommentInThreadMutation.isLoading;

  const handleCommentOpenerClick = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  const handlePopoverClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleAddCommentClick = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      if (!inputRef.current?.value?.trim().length) return;

      const newCommentData: SchemaCommentThreadCreateSchema & { text: string } = {
        fund_id: fundId ?? null,
        field_name: fieldName ?? null,
        initial_message: inputRef.current.value,
        text: inputRef.current.value,
        fund_name: formattedFundName ?? null,
        workflow_id: workflowId ?? null,
        table_name: tableName ?? null,
        order: fieldOrder ? `${fieldOrder}` : null,
        period_id: periodId ?? null,
        report_type: currentReportType
      };

      // thread has not been created yet
      if (!cellThreadData.id) {
        createThreadMutation.mutate(
          { data: newCommentData },
          {
            onSuccess: () => {
              if (inputRef.current) {
                inputRef.current.value = '';
              }
              refetchComments();
              queryClient.refetchQueries({
                queryKey: [QUERY_KEYS.GET_ALL_COMMENTS, 'REPORT_COMMENTS'],
                exact: false
              });
            },
            onError: () => {}
          }
        );
      } else {
        createCommentInThreadMutation.mutate(
          { data: newCommentData, threadID: cellThreadData.id },
          {
            onSuccess: () => {
              if (inputRef.current) {
                inputRef.current.value = '';
              }
              refetchComments();
              queryClient.refetchQueries({
                queryKey: [QUERY_KEYS.GET_ALL_COMMENTS, 'REPORT_COMMENTS'],
                exact: false
              });
            },
            onError: () => {}
          }
        );
      }
    },
    [
      fundId,
      formattedFundName,
      fieldName,
      fieldOrder,
      tableName,
      workflowId,
      periodId,
      currentReportType,
      cellThreadData.id,
      createThreadMutation,
      createCommentInThreadMutation,
      refetchComments
    ]
  );

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputRef.current?.value?.trim().length) {
      handleAddCommentClick(e);
    }
  };

  return (
    <Styled.ReportCellCommentContainer
      // ⚠️ Do not remove className here
      // it's used to control ReportCellCommentContainer visibility in other components (see NewTextCellTemplate/style.js)
      className="cell-comment-container"
      // @ts-ignore
      isAlwaysVisible={cellThreadComments.length > 0}
      id={`cell-comment-${cellThreadData.id}`}
    >
      <Popover
        isOpen={isOpen}
        onClickOutside={handlePopoverClose}
        positions={['left']}
        padding={5}
        containerStyle={{ zIndex: '1000' }}
        content={
          <Styled.MessageContainer id={`cell-comment-popover-${cellThreadData.id}`}>
            <Styled.MessageHeader id={`cell-comment-popover-header-${cellThreadData.id}`}>
              <div className="title" id={`cell-comment-popover-title-${cellThreadData.id}`}>
                <TbMessageCircle color="#ADB5BD" />
                Comments
              </div>
              <div className="actions" id={`cell-comment-popover-actions-${cellThreadData.id}`}>
                <CommentResolvedState
                  isResolved={cellThreadData.is_resolved}
                  threadID={cellThreadData.id}
                  refetchComments={refetchComments}
                />
                <IoClose
                  cursor="pointer"
                  onClick={handlePopoverClose}
                  fontSize={20}
                  color="#868E96"
                />
              </div>
            </Styled.MessageHeader>

            <CellCommentsList comments={cellThreadComments} refetchComments={refetchComments} />

            <Styled.MessageFooter id={`cell-comment-popover-footer-${cellThreadData.id}`}>
              <input
                id={`cell-comment-input-${cellThreadData.id}`}
                onPointerDown={e => e.stopPropagation()}
                onKeyDown={handleInputKeyDown}
                ref={inputRef}
                placeholder="Leave a comment..."
              />
              <Styled.AddCommentBtn
                id={`cell-comment-add-button-${cellThreadData.id}`}
                disabled={isCreatingNewThreadOrComment}
                onClick={handleAddCommentClick}
                type="button"
              >
                Add Comment
              </Styled.AddCommentBtn>
            </Styled.MessageFooter>
          </Styled.MessageContainer>
        }
      >
        <Styled.CommentIconContainer
          id={`cell-comment-icon-container-${cellThreadData.id}`}
          data-testid="comment-icon-container"
          onPointerDown={handleCommentOpenerClick}
        >
          {cellThreadComments.length > 0 && (
            <span
              id={`cell-comment-count-${cellThreadData.id}`}
              data-testid="comment-count"
              className="comment-count"
            >
              {cellThreadComments.length}
            </span>
          )}
          <TbMessageCircle color="#ADB5BD" />
        </Styled.CommentIconContainer>
      </Popover>
    </Styled.ReportCellCommentContainer>
  );
};

export default memo(ReportCellComment);
